import { AccountRollup, WindowSummary } from './flock-telemetry.models';
import { isAuthoritativeQuotaSource } from './flock-telemetry.utils';

/**
 * Per-seat 5h / 7d usage for the seat viewer (Bug 37596).
 *
 * A seat's context snapshot carries 5h / 7d only when the Control Center on
 * the seat's own host read them from a local statusline payload, so seats on
 * other hosts read blank, and a seat that just switched account by /login can
 * post a fresh 0. Both windows are ACCOUNT-level, so the bridge's quota
 * readings for the seat's current account (the rollups the Accounts page
 * already shows) are the source of truth. Per window: the account's live API
 * reading wins; otherwise the seat keeps its own value. A token estimate never
 * replaces a reading, and an expired window is never shown as live.
 */

/** The usage fields a seat row must carry to be joined. */
export interface SeatUsageInput {
  accountUpn: string | null;
  fiveHourPercent: number | null;
  sevenDayPercent: number | null;
}

/** What the join adds: when each shown reading was taken, and its source. */
export interface SeatUsageFields {
  /** Capture time of the account reading shown for 5h; null when the value
   * is the seat's own (or absent). */
  fiveHourAt: string | null;
  sevenDayAt: string | null;
  /** 'account' when either window came from the account's bridge readings,
   * 'seat' when only the seat's own values are shown, null when neither. */
  usageSource: 'account' | 'seat' | null;
}

/** The window's live API reading, or null (expired, estimate-only, none). */
function liveReading(
  w: WindowSummary | undefined
): { percent: number; at: string | null } | null {
  if (!w || w.staleObservation || !isAuthoritativeQuotaSource(w)) {
    return null;
  }
  return { percent: w.percentUsed, at: w.capturedAt ?? null };
}

/**
 * Returns new rows (the input is not mutated) with fiveHourPercent /
 * sevenDayPercent filled from the seat's account rollup where it holds a live
 * API reading, plus the capture times and source of what is shown.
 */
export function withAccountUsage<T extends SeatUsageInput>(
  rows: readonly T[],
  rollups: readonly AccountRollup[]
): (T & SeatUsageFields)[] {
  const byAccount = new Map<string, AccountRollup>();
  for (const r of rollups) byAccount.set(r.accountUpn.toLowerCase(), r);
  return rows.map((row) => {
    const rollup = row.accountUpn
      ? byAccount.get(row.accountUpn.toLowerCase())
      : undefined;
    const five = liveReading(rollup?.fiveHour);
    const seven = liveReading(rollup?.weekly);
    const fiveHourPercent = five ? five.percent : row.fiveHourPercent;
    const sevenDayPercent = seven ? seven.percent : row.sevenDayPercent;
    const usageSource: SeatUsageFields['usageSource'] =
      five || seven
        ? 'account'
        : fiveHourPercent != null || sevenDayPercent != null
          ? 'seat'
          : null;
    return {
      ...row,
      fiveHourPercent,
      sevenDayPercent,
      fiveHourAt: five ? five.at : null,
      sevenDayAt: seven ? seven.at : null,
      usageSource,
    };
  });
}
